import React from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { DotsThree, Sidebar, Strategy } from "@phosphor-icons/react";

import DomainPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/DomainPill";
import StatusPill from "components/ClientDetails/Sections/GoalsAndTargets/shared/StatusPill";
import ClinicalPhasePill from "components/ClientDetails/Sections/GoalsAndTargets/shared/ClinicalPhasePill";
import type { ClinicalPhase } from "components/ClientDetails/Sections/GoalsAndTargets/shared/types";

// TODO: implement "Add to Program" functionality and remove this condition when ready.
const HIDE_ADD_TO_PROGRAM = true;

export interface GoalDetailTitleBarProps {
  /** Full display name of the goal */
  goalName: string;
  /** Long-form goal description shown below the title */
  description: string;
  /** Clinical domain of the goal */
  domain?: DomainId;
  /** Current clinical phase of the goal */
  clinicalPhase?: ClinicalPhase;
  /** Current status of the goal */
  status: StatusId;
  /** Called when the user clicks "Add to Program" */
  onAddToProgram?: () => void;
  /** Called when the overflow menu button is clicked */
  onMenuOpen?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Called when the sidebar collapse toggle is clicked. The toggle only
   * renders when this callback is provided (e.g. the target detail view).
   */
  onToggleSidebar?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Current collapsed state of the sidebar — drives the toggle's label. */
  isSidebarCollapsed?: boolean;
}

/**
 * Title bar for the GoalDetail view.
 * Renders the goal name, domain pill, status pill, and description on the left,
 * with an "Add to Program" action and overflow menu on the right.
 *
 * @param props
 * @param props.goalName        - Full display name of the goal
 * @param props.description     - Long-form goal description
 * @param props.domain          - Clinical domain
 * @param props.clinicalPhase   - Current clinical phase of the goal
 * @param props.status          - Current goal status
 * @param props.onAddToProgram  - Callback for the "Add to Program" button
 * @param props.onMenuOpen      - Callback for the overflow menu button
 */
const GoalDetailTitleBar: React.FC<GoalDetailTitleBarProps> = ({
  goalName,
  description,
  domain,
  clinicalPhase,
  status,
  onAddToProgram,
  onMenuOpen,
  onToggleSidebar,
  isSidebarCollapsed = false,
}) => {
  return (
    <Box
      component="header"
      aria-label="Goal details header"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 3,
        width: "100%",
      }}
    >
      {/* Left: title row + description */}
      <Stack direction="column" gap={0} sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Typography component="h1" variant="h3" color="text.primary">
            {goalName}
          </Typography>
          {clinicalPhase ? <ClinicalPhasePill phase={clinicalPhase} /> : null}
          {domain ? <DomainPill domain={domain} /> : null}
          <StatusPill status={status} />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>

      {/* Right: actions */}
      <Stack direction="row" alignItems="center" gap={3} flexShrink={0}>
        {!HIDE_ADD_TO_PROGRAM && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<Strategy size={16} />}
            onClick={onAddToProgram}
            aria-label="Add goal to program"
          >
            Add to Program
          </Button>
        )}

        {onToggleSidebar && (
          <IconButton
            size="small"
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={isSidebarCollapsed}
            onClick={onToggleSidebar}
          >
            <Sidebar size={24} />
          </IconButton>
        )}

        <IconButton size="small" aria-label="Goal options" onClick={onMenuOpen}>
          <DotsThree size={24} weight="bold" />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default GoalDetailTitleBar;
